import {
  Button,
  Grid2,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  OrganizationState,
  useOrganizationState,
} from 'features/organizations/state/useOrganizationState';
import RecurlyCardElement from 'components/RecurlyBillingInformation/RecurlyCardElement';
import { Controller, useForm } from 'react-hook-form';
import { useRecurly } from '@recurly/react-recurly';
import { getCreditCardValidationErrors } from 'components/RecurlyBillingInformation/utils';
import { useOrganizationUpgrade } from 'features/organizations/hooks/useOrganizationUpgrade';
import { useState, useRef } from 'react';
import { enqueueSnackbar } from 'notistack';
import * as Sentry from '@sentry/capacitor';
import { useOrganizationDetails } from 'features/organizations/hooks/useOrganizationDetails';
import { LoadingAndErrors } from 'components/LoadingAndErrors';
import usePaymentMethodQuery from 'hooks/usePaymentMethodQuery';
import { RecurlySavedPayment } from 'components/RecurlySavedPayment';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { OrganizationUpgradeSummary } from '../OrganizationUpgradeSummary';

interface OrganizationUpgradeBillingData {
  postalCode: string;
}

type OrganizationUpgradeBillingInfoFormProps = {
  onSubmit: (values: Partial<OrganizationState>) => void;
};

export const OrganizationUpgradeBillingInfoForm = (
  props: OrganizationUpgradeBillingInfoFormProps,
): JSX.Element => {
  const { onSubmit } = props;
  const { t } = useTranslation();
  const recurly = useRecurly();
  const [errors, setErrors] = useState<string[]>([]);
  const { processOrganizationUpgrade } = useOrganizationUpgrade({});
  const orgState = useOrganizationState();
  const useNewPaymentMethodRef = useRef(false);

  const { data: savedPaymentMethod, isLoading: isLoadingPaymentMethod } =
    usePaymentMethodQuery({ useLoggedInState: true });

  const {
    details,
    isLoading: detailsLoading,
    isError: detailsError,
  } = useOrganizationDetails();

  const { control, handleSubmit, formState } =
    useForm<OrganizationUpgradeBillingData>({
      defaultValues: {
        postalCode: orgState.postalCode || '',
      },
    });

  const hasErrors = errors.length > 0;
  const hasSavedPaymentMethod = !!(
    savedPaymentMethod && savedPaymentMethod.paymentType
  );

  const submitToken = async (
    values: OrganizationUpgradeBillingData,
    authorizationToken?: string,
  ): Promise<void> => {
    if (!details) {
      throw new Error('details-not-loaded');
    }

    const { seats } = details;
    const totalRegularSeats = seats.regular.purchased + orgState.regularSeats;
    const totalProSeats = seats.pro.purchased + orgState.proSeats;

    await processOrganizationUpgrade({
      ...(authorizationToken ? { authorizationToken } : {}),
      regularSeats: totalRegularSeats,
      proSeats: totalProSeats,
    });

    onSubmit({
      ...orgState,
      ...values,
    });
  };

  const handleFormSubmit = async (
    values: OrganizationUpgradeBillingData,
  ): Promise<void> => {
    try {
      if (hasSavedPaymentMethod && !useNewPaymentMethodRef.current) {
        await submitToken(values);
      } else {
        // Generate new payment token
        const token = await new Promise<string>((resolve, reject) => {
          recurly.token(
            {
              first_name: orgState.firstName,
              last_name: orgState.lastName,
              postal_code: values.postalCode,
            },
            (err, recurlyToken) => {
              if (err) {
                const validationErrors = getCreditCardValidationErrors(err, t);
                setErrors(validationErrors);
                reject(err);
              } else {
                resolve(recurlyToken.id);
              }
            },
          );
        });
        await submitToken(values, token);
      }
    } catch (error) {
      Sentry.captureException(error);
      enqueueSnackbar(t('common.unknownErrorTryAgain'), { variant: 'error' });
    }
  };

  const handleToggleUseNewPaymentMethod = (): void => {
    useNewPaymentMethodRef.current = !useNewPaymentMethodRef.current;
    setErrors([]);
  };

  if (detailsLoading || detailsError || isLoadingPaymentMethod) {
    return <LoadingAndErrors isLoading />;
  }

  return (
    <Grid2
      container
      direction="column"
      spacing={2}
      sx={{ flex: 1, pt: 1 }}
      component="form"
      noValidate
      onSubmit={handleSubmit(handleFormSubmit)}
    >
      <Grid2 container direction="column" spacing={2}>
        <Grid2>
          <Typography variant="h3">
            <b>{t('organizationPurchase.billingInfo')}</b>
          </Typography>
        </Grid2>

        {hasSavedPaymentMethod && !useNewPaymentMethodRef.current ? (
          <>
            <Grid2>
              <RecurlySavedPayment paymentMethod={savedPaymentMethod} />
            </Grid2>
            {/* Using structure from exsting RecurlyAccountInformation page */}
            <Grid2>
              <Typography variant="h3" sx={{ marginTop: 4 }}>
                {t('organizationPurchase.updatePaymentMethod')}
              </Typography>
            </Grid2>

            <Grid2>
              <List>
                <ListItem disablePadding>
                  <ListItemButton onClick={handleToggleUseNewPaymentMethod}>
                    <ListItemText
                      primary={t('recurlyAccountInformation.button')}
                    />
                    <ListItemIcon sx={{ minWidth: 0 }}>
                      <ArrowForwardIosIcon color="inherit" />
                    </ListItemIcon>
                  </ListItemButton>
                </ListItem>
              </List>
            </Grid2>
          </>
        ) : (
          <>
            <Grid2>
              <RecurlyCardElement error={hasErrors} />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                name="postalCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    label={t('organizationPurchase.zipcode.label')}
                    required
                    {...field}
                  />
                )}
              />
            </Grid2>
          </>
        )}

        <OrganizationUpgradeSummary
          regularSeats={orgState.regularSeats}
          proSeats={orgState.proSeats}
        />
      </Grid2>
      <Grid2
        size="grow"
        container
        direction="column"
        sx={{ justifyContent: 'flex-end' }}
      >
        <Grid2>
          <Button
            size="large"
            fullWidth
            type="submit"
            disabled={formState.isSubmitting}
          >
            {t('organizationPurchase.purchase')}
          </Button>
        </Grid2>
      </Grid2>
    </Grid2>
  );
};
